/**
 * requisiciones.ts
 * Compras (REQUI) module — Requisiciones section: case/item creation input,
 * case/item/detail/event response shapes, case timeline, pipeline + my-cases
 * listings, seed test tooling, and the requisition-options "assets" lookup
 * (requisition-options.routes.ts).
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';

import { UnitSchema, ModalidadSchema, ITEM_KINDS, DemandOriginSchema, PRIORITIES, EVENT_CLASSES } from './shared.js';

export const CaseItemCreateSchema = z.object({
  description: z.string().min(1),
  kind: z.enum(ITEM_KINDS),
  /**
   * alta-solicitud.md §3.2 "Texto libre gobernado" (2026-08-13): now optional
   * — "Pedir algo que no está en el catálogo" creates an item with NO
   * categoryId. `undefined` here means the item is born `pendiente_normalizar`
   * (application layer, CreateCaseUseCase). No extra .refine() is needed for
   * §3.2's "descripción libre, tipo, cantidad y unidad" minimum: those four
   * fields (description/kind/qty/unit below) are already unconditionally
   * required by this schema regardless of categoryId, so a categoryId-less
   * item automatically satisfies the free-text minimum.
   */
  categoryId: z.string().uuid().optional(),
  subrubro: z.string().optional(),
  qty: z.number().positive(),
  unit: UnitSchema,
  /**
   * 2026-09-04-requi-solicitud-vehiculo (compras-item-asset-link AL-2) — set
   * when the item's rubro requires a vehicle/equipment. Shape only: this
   * schema never knows whether the chosen `categoryId` is gated — the
   * `requiresAsset` gate (missing/unresolvable assetId for a gated rubro)
   * is enforced at the application layer (CreateCaseUseCase.createCaseInTx),
   * not here (design D1 — Zod is sync/pure, has no DB or tenant context).
   */
  assetId: z.string().uuid().optional(),
  /** alta-solicitud.md §2/§4 — "Para la bomba del Pad 7", editable inline por fila. */
  observacion: z.string().optional(),
  /**
   * Migration 0136 — modalidad of the line. Defaults to 'compra' at the
   * application layer when omitted. `rentalStart`/`rentalEnd` (ISO date
   * strings) only apply when modalidad === 'alquiler' — the application
   * layer ignores them otherwise rather than failing case creation.
   */
  modalidad: ModalidadSchema.optional(),
  rentalStart: z.string().optional(),
  rentalEnd: z.string().optional(),
  /**
   * Migration 0129 — set when the item came from the alta-solicitud.md §3.1
   * buscador (item_master, renamed from item_catalogo by migration 0137), so
   * description/kind/categoryId/unit were inherited rather than typed
   * manually. `undefined` for a free-text item, exactly like today.
   */
  itemMasterId: z.string().uuid().optional(),
});
export type CaseItemCreate = z.infer<typeof CaseItemCreateSchema>;

export const CaseCreateSchema = z.object({
  /**
   * alta-solicitud.md decision (2026-08-12, "Título" sale del form): the
   * manual creation form no longer collects a title — the backend derives
   * one (see CreateCaseUseCase.createCaseInTx). `reference` below is the
   * genuinely-optional override.
   */
  reference: z.string().optional(),
  costCenterId: z.string().uuid().optional(),
  deliveryLocationId: z.string().uuid().optional(),
  /** Migration 0010 — the operator's Área / Yacimiento (business unit) this SA is for; catalog.area. */
  areaId: z.string().uuid().optional(),
  currency: z.string().optional(),
  /** Bare uuid, no FK — set only when this case originates from a LOCX "Solicitar compra". */
  sourceServiceOrderId: z.string().uuid().optional(),
  /**
   * Tablero de Compras (migration 0125) — real columns, "próximos vencimientos"
   * widget. alta-solicitud.md §2: "Obligatorio" — now required (was optional).
   */
  neededBy: z.string().min(1),
  priority: z.enum(PRIORITIES).optional(),
  /** Migration 0133 — alta-solicitud.md §2/§4. Required: every case has a demand origin. */
  demandOrigin: DemandOriginSchema,
  /** Migration 0133 — free text, no validated OT/obra reference yet (see migration comment). */
  otReference: z.string().optional(),
  clientName: z.string().optional(),
  items: z.array(CaseItemCreateSchema).min(1),
});
export type CaseCreate = z.infer<typeof CaseCreateSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const CaseResponseSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  /**
   * Migration 0135 — alta-solicitud.md decision (2026-08-12). Free-text
   * override for the case's display label — when set, shown INSTEAD of
   * `title` everywhere the label is rendered (`reference ?? title`).
   */
  reference: z.string().nullable(),
  status: z.string(),
  requesterId: z.string().uuid(),
  approverId: z.string().uuid().nullable(),
  buyerId: z.string().uuid().nullable(),
  costCenterId: z.string().uuid().nullable(),
  deliveryLocationId: z.string().uuid().nullable(),
  areaId: z.string().uuid().nullable(),
  currency: z.string(),
  estimatedTotal: z.string().nullable(),
  sourceServiceOrderId: z.string().uuid().nullable(),
  // Tablero de Compras (migration 0125) — DB-nullable columns, always .nullable()
  // in response schemas (this file's convention), never .optional().
  neededBy: z.string().nullable(),
  priority: z.enum(PRIORITIES).nullable(),
  // Migration 0133 — same .nullable() convention. Not rendered by the case
  // detail header in this pass (alta-solicitud.md §2/§4 scope), but the
  // contract can't hide data that already exists on the model.
  demandOrigin: DemandOriginSchema.nullable(),
  otReference: z.string().nullable(),
  clientName: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  // Cabecera de contexto (originally GET /procurement/cases/:id only —
  // plan-accion-compras.md PASO 4). .optional() (not just .nullable()) so
  // producers that don't resolve these fields (case creation response, etc.)
  // don't need to start emitting them — they simply omit them.
  // requesterName (compras-solicitudes-visibilidad-alertas, Slice A) is ALSO
  // populated by GET /procurement/cases and GET /procurement/my-cases now —
  // PurchaseCaseRepository.listPipeline() batch-resolves it via the kernel
  // lookupUsersByIds port. costCenterName/categoryLabel remain detail-only.
  requesterName: z.string().nullable().optional(),
  costCenterName: z.string().nullable().optional(),
  /** Detail-only, like costCenterName — the Área / Yacimiento's name. */
  areaName: z.string().nullable().optional(),
  categoryLabel: z.string().nullable().optional(),
  /**
   * alta-solicitud.md §3.2/§3.3 — count of this case's items with
   * `pendiente_normalizar = true` (texto libre gobernado, sin rubro). Always
   * present (never .optional(), unlike the trio above which only the detail
   * endpoint fills in) — every producer of CaseResponse must supply a real
   * number, 0 when none. Backs the amber badge on PipelineList.vue; the
   * frontend decides whether to render it, this field never omits itself.
   */
  pendingClassificationCount: z.number(),
  /**
   * Pipeline rows only: the earliest OC approval still pending among the
   * case's items ('n1' Compras, 'n2' Gerencia), null when none. Feeds the
   * list's progress dots; the detail derives it from its items.
   */
  poApprovalWaiting: z.enum(['n1', 'n2']).nullable().optional(),
});
export type CaseResponse = z.infer<typeof CaseResponseSchema>;

export const CaseItemResponseSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  lineNo: z.number(),
  description: z.string(),
  kind: z.enum(ITEM_KINDS),
  /** Migration 0136 — split out of `kind`. Always present (DB default 'compra'). */
  modalidad: ModalidadSchema,
  rentalStart: z.string().nullable(),
  rentalEnd: z.string().nullable(),
  /**
   * Migration 0134 — nullable at the DB level since that migration;
   * alta-solicitud.md §3.2 "texto libre gobernado" (2026-08-13) is the first
   * flow that actually PRODUCES a null here (an item born without rubro).
   */
  categoryId: z.string().uuid().nullable(),
  subrubro: z.string().nullable(),
  qty: z.string(),
  qtyPendiente: z.string(),
  /**
   * Migration 0206 (requi-consolicacion-multirubro T9) — the ORIGINAL
   * requested qty, written once at item creation and never touched again;
   * `qty` above may since have been adjusted (AdjustItemQuantityUseCase,
   * only while `pendiente`). The frontend shows "Pedido: X → A comprar: Y"
   * when the two differ.
   */
  requestedQuantity: z.string(),
  unit: z.string(),
  status: z.string(),
  parentItemId: z.string().uuid().nullable(),
  pooled: z.boolean(),
  /**
   * Migration 0134 column, first written for real by §3.2's texto libre
   * gobernado — true for an item born without rubro (categoryId null), until
   * the comprador classifies it (paso ①, item_clasificado event) and it
   * flips back to false alongside categoryId being set.
   */
  pendienteNormalizar: z.boolean(),
  /**
   * Migration 0129/0137 — requi-inventario-stock PR 3: the comprador's
   * bandeja needs this to know which lines can show/consume stock (join key
   * for GET /procurement/stock/balances) — null for a free-text item (never
   * linked to item_master), exactly like CaseItemCreateSchema's own
   * `itemMasterId` above.
   */
  itemMasterId: z.string().uuid().nullable(),
  createdAt: z.string(),
  /**
   * "Mis solicitudes" (GET /procurement/my-cases) — same .optional() posture
   * as requesterName/costCenterName above: only that endpoint's producer
   * populates these two, the pipeline/detail/approvals producers omit them.
   * `cancelReason` mirrors the DB column 1:1 (CancelReasonSchema, above) —
   * 'hay_en_stock' is how the frontend tells "abastecido desde pañol" apart
   * from every other cancellation (openspec/auditorias/2026-08-26-kernel-
   * capabilities-permisos.md §2 item 9's solicitante-facing vocabulary).
   */
  cancelReason: z.string().nullable().optional(),
  /**
   * Derived, NOT persisted — the AWARDED quote_line's quotation.deliveryDays
   * (GetMyRequestsUseCase), only resolved for an item already past
   * adjudicación (adjudicado/en_oc/recibido). `null` before that, or when no
   * quote_line carries a deliveryDays value.
   */
  estimatedDeliveryDays: z.number().nullable().optional(),
  /**
   * compras-inventory-stock REQ-60 / compras-http-routes REQ-2X — only the
   * `recepcion_registrada` (T16) response producer populates this; every
   * other event/list producer omits it (same `.optional()` posture as
   * `cancelReason`/`estimatedDeliveryDays` above). When present it is NEVER
   * omitted or null — `RegisterReceptionUseCase` always returns an explicit
   * boolean so the caller never infers "stock moved" from `status` alone
   * (design.md D12 — this is the bug PR 0 fixes).
   */
  stockApplied: z.boolean().optional(),
});
export type CaseItemResponse = z.infer<typeof CaseItemResponseSchema>;

export const CaseDetailResponseSchema = z.object({
  case: CaseResponseSchema,
  items: z.array(CaseItemResponseSchema),
});
export type CaseDetailResponse = z.infer<typeof CaseDetailResponseSchema>;

export const CaseEventResponseSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  itemId: z.string().uuid().nullable(),
  seq: z.number(),
  type: z.string(),
  class: z.enum(EVENT_CLASSES),
  actorId: z.string().uuid().nullable(),
  actorRole: z.string().nullable(),
  payload: z.record(z.unknown()),
  createdAt: z.string(),
});
export type CaseEventResponse = z.infer<typeof CaseEventResponseSchema>;

// ---------------------------------------------------------------------------
// Sesión 3 — proyecciones de solo lectura (SDD §11/§12 paso 4).
// ---------------------------------------------------------------------------

/** GET /procurement/cases/:id/timeline — "eventos proyectados (clasificados)". */
export const CaseTimelineSummarySchema = z.object({
  totalCount: z.number(),
  rutinaCount: z.number(),
  decisionCount: z.number(),
  anomaliaCount: z.number(),
  lastEvent: CaseEventResponseSchema.optional(),
});
export type CaseTimelineSummary = z.infer<typeof CaseTimelineSummarySchema>;

export const CaseTimelineResponseSchema = z.object({
  events: z.array(CaseEventResponseSchema),
  summary: CaseTimelineSummarySchema,
});
export type CaseTimelineResponse = z.infer<typeof CaseTimelineResponseSchema>;

/** GET /procurement/cases (pipeline) + GET /procurement/approvals/pending — both paginated CaseResponse lists. */
export const CasePipelineResponseSchema = z.object({
  items: z.array(CaseResponseSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type CasePipelineResponse = z.infer<typeof CasePipelineResponseSchema>;

/**
 * GET /procurement/my-cases — "Mis solicitudes" (requi.solicitud.leer-propias,
 * openspec/auditorias/2026-08-26-kernel-capabilities-permisos.md §2 item 9).
 * One row per own case WITH its items nested (unlike CasePipelineResponse,
 * flat cases only) — a solicitante needs "cuándo llega" per item, which lives
 * on CaseItemResponse.estimatedDeliveryDays/cancelReason, not on the case.
 */
export const MyCaseResponseSchema = z.object({
  case: CaseResponseSchema,
  items: z.array(CaseItemResponseSchema),
});
export type MyCaseResponse = z.infer<typeof MyCaseResponseSchema>;

export const MyCasesResponseSchema = z.object({
  items: z.array(MyCaseResponseSchema),
  totalCount: z.number(),
  totalPages: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type MyCasesResponse = z.infer<typeof MyCasesResponseSchema>;

// ---------------------------------------------------------------------------
// requi-consolidacion-multirubro T10 — seed test tooling.
// ---------------------------------------------------------------------------

/**
 * GET /procurement/seed-cases/status — whether `compras.seed_tools.enabled` is
 * on for this tenant, plus (T10 delete half, migration 0208) how many seed SAs
 * exist right now, so the UI can show "Borrar SAs semilla (N)" and disable it
 * at N = 0 without a second round trip.
 */
export const SeedCasesStatusResponseSchema = z.object({ enabled: z.boolean(), seedCaseCount: z.number() });
export type SeedCasesStatusResponse = z.infer<typeof SeedCasesStatusResponseSchema>;

/** POST /procurement/seed-cases — result of SeedTestCasesUseCase. */
export const SeedCasesResponseSchema = z.object({
  casesCreated: z.number(),
  itemsCreated: z.number(),
  caseCodes: z.array(z.string()),
});
export type SeedCasesResponse = z.infer<typeof SeedCasesResponseSchema>;

/** DELETE /procurement/seed-cases (T10 delete half) — result of DeleteSeedCasesUseCase. */
export const SeedCasesDeleteResponseSchema = z.object({
  casesDeleted: z.number(),
  itemsDeleted: z.number(),
  roundsDeleted: z.number(),
  ocsDeleted: z.number(),
});
export type SeedCasesDeleteResponse = z.infer<typeof SeedCasesDeleteResponseSchema>;

// ---------------------------------------------------------------------------
// 2026-09-04-requi-solicitud-vehiculo — GET /compras/requisition-options/assets
// response row (compras-item-asset-link AL-3, compras-http-routes). Structural
// mirror of `IAssetLookupPort.AssetRow` — this package never imports the
// servicios/compras-domain type, same posture as every other contracts-*
// mirror in this codebase (e.g. StockDispatchLineSchema's header comment).
// ---------------------------------------------------------------------------
export const AssetOptionSchema = z.object({
  id: z.string().uuid(),
  serialNumber: z.string(),
  description: z.string().nullable(),
  assetTypeId: z.string().uuid(),
  /** asset_type.label, resolved by the servicios adapter — lets the picker group by type (AL-3.5). */
  assetTypeName: z.string(),
  status: z.string(),
});
export type AssetOption = z.infer<typeof AssetOptionSchema>;

export const AssetOptionsResponseSchema = z.object({
  items: z.array(AssetOptionSchema),
});
export type AssetOptionsResponse = z.infer<typeof AssetOptionsResponseSchema>;
